import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@api/api";
import NotificationMenu from "./NotificationMenu";

export default function NotificationContainer({ perfil }) {
  const navigate = useNavigate();

  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const ultimaSolicitacao = useRef(0);

  const carregarNotificacoes = useCallback(async () => {
    const solicitacao = ++ultimaSolicitacao.current;

    setLoading(true);
    setError("");

    try {
      const response = await fetch(api.notificationsFeed(), {
        credentials: "include",
      });

      if (!response.ok) {
        throw new Error("Não foi possível carregar as notificações.");
      }

      const data = await response.json();

      if (solicitacao === ultimaSolicitacao.current) {
        setNotifications(Array.isArray(data) ? data : []);
      }
    } catch (err) {
      if (solicitacao === ultimaSolicitacao.current) {
        setError(
          err instanceof Error ? err.message : "Erro ao carregar notificações.",
        );
      }
    } finally {
      if (solicitacao === ultimaSolicitacao.current) {
        setLoading(false);
      }
    }
  }, []);

  useEffect(() => {
    carregarNotificacoes();

    const conexao = new EventSource(api.notificationsEvents(), {
      withCredentials: true,
    });

    conexao.onopen = carregarNotificacoes;
    conexao.onmessage = carregarNotificacoes;

    const atualizarAoVoltar = () => {
      if (document.visibilityState === "visible") {
        carregarNotificacoes();
      }
    };

    document.addEventListener("visibilitychange", atualizarAoVoltar);

    return () => {
      conexao.close();
      document.removeEventListener("visibilitychange", atualizarAoVoltar);
    };
  }, [carregarNotificacoes]);

  const marcarComoLida = async (id) => {
    try {
      const response = await fetch(api.notificationRead(id), {
        method: "PATCH",
        credentials: "include",
      });

      if (!response.ok) {
        throw new Error("Não foi possível marcar a notificação como lida.");
      }

      ultimaSolicitacao.current += 1;
      setLoading(false);

      setNotifications((atuais) =>
        atuais.map((item) => (item.id === id ? { ...item, lida: true } : item)),
      );

      setError("");
      return true;
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Erro ao marcar como lida.",
      );
      return false;
    }
  };

  const marcarTodasComoLidas = async () => {
    try {
      const response = await fetch(api.notificationsReadAll(), {
        method: "PATCH",
        credentials: "include",
      });

      if (!response.ok) {
        throw new Error("Não foi possível marcar todas como lidas.");
      }

      await carregarNotificacoes();
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Erro ao marcar como lidas.",
      );
    }
  };

  const limparTodas = async () => {
    try {
      const response = await fetch(api.notificationsClear(), {
        method: "DELETE",
        credentials: "include",
      });

      if (!response.ok) {
        throw new Error("Não foi possível limpar as notificações.");
      }

      await carregarNotificacoes();
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Erro ao limpar notificações.",
      );
    }
  };

  const abrirDestino = async (item) => {
    if (
      item.resolvida_em ||
      ["cadastro_recusado", "processo_rejeitado"].includes(item.tipo) ||
      !["analyst", "user"].includes(perfil)
    ) {
      return false;
    }

    const id = Number(
      item.origem_tipo === "documento"
        ? item.processo_id
        : (item.origem_id ?? item.processo_id),
    );

    if (!Number.isInteger(id) || id <= 0) {
      return false;
    }

    let destino;

    if (perfil === "user") {
      if (["processo", "documento"].includes(item.origem_tipo)) {
        destino = `/detalhes-processo/${id}`;
      } else if (
        ["escola", "mantenedora"].includes(item.origem_tipo) &&
        item.tipo === "cadastro_aprovado"
      ) {
        destino = "/home/perfil";
      } else {
        return false;
      }
    } else if (["processo", "documento"].includes(item.origem_tipo)) {
      destino = `/processo-detalhado/${id}`;
    } else if (["escola", "mantenedora"].includes(item.origem_tipo)) {
      if (item.tipo === "cadastro_aprovado") {
        destino =
          item.origem_tipo === "escola"
            ? `/perfil-escola/${id}`
            : `/perfil-mantenedora/${id}`;
      } else if (item.tipo === "cadastro_pendente") {
        destino = `/aprovar-cadastro/${item.origem_tipo}/${id}`;
      } else {
        return false;
      }
    } else {
      return false;
    }

    if (
      (perfil === "user" && item.tipo === "documento_devolvido") ||
      (perfil === "analyst" && item.tipo === "documento_reenviado")
    ) {
      const notificacaoOriginal = notifications.find(
        (notificacao) => notificacao.id === item.id,
      );

      const documentoId = Number(notificacaoOriginal?.origem_id);

      if (
        notificacaoOriginal?.origem_tipo === "documento" &&
        Number.isInteger(documentoId) &&
        documentoId > 0 &&
        Number(notificacaoOriginal.processo_id) === id
      ) {
        destino += `?documento=${documentoId}`;
      }
    }

    if (!item.lida && !(await marcarComoLida(item.id))) {
      return false;
    }

    navigate(destino);
    return true;
  };

  return (
    <NotificationMenu
      perfil={perfil}
      notifications={notifications}
      loading={loading}
      error={error}
      onOpen={carregarNotificacoes}
      onRead={marcarComoLida}
      onReadAll={marcarTodasComoLidas}
      onClear={limparTodas}
      onView={abrirDestino}
    />
  );
}
